import React, { useEffect, useMemo, useRef, useState } from "react";
import { Divider, Box, Button, Stack, Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import {
  RiArrowLeftSFill,
  RiArrowRightSFill,
  RiPushpinFill,
} from "react-icons/ri";

import YesOrNoSwitch from "../../../UI/YesOrNoSwitch";
import { VerticalTab } from "../../../VerticalTabs";
import LeftSideBarHeader from "../LeftSideBarHeader";
import CalcOption from "../LeftSideBarHeader/CalcOption";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import { useProject } from "../../../../contexts/project.context";
import { isAbbottClient } from "../../../../constants/abbottClient";
import { BudgetIcons } from "../../../../assets/styled-icons/budget";
import BudgetTotals from "../BudgetTotals/BudgetTotals";
import {
  BudgetItemTypeEnum,
  BudgetStatus,
  HeaderOptions,
} from "../../../../types/budget/budget";
import { createOptionsFromEnum } from "../../../../utils/formatters";
import { ReactComponent as DescriptionIcon } from "../../../../assets/styled-icons/Calendar.svg";
import BudgetDataModal from "../../../Project/components/BudgetDataModal";
import { TabActive } from "../../../../types";
import { ReactComponent as Unpinned } from "../../../../assets/styled-icons/unpinned.svg";
import { FaThumbtack } from "react-icons/fa";
import LeftSideBarHeaderCollapsed from "../LeftSideBarHeader/Collapsed";
import { sortItemsByCategory } from "../../../../utils/category-sort";

type CategoriesSelectionProps = {
  onHoverChange?: (hovering: boolean) => void;
  onCollapseChange?: (collapsed: boolean) => void;
  disabled?: boolean;
};

function findLastApprovedVersion(
  versionHistory: { status?: string; version?: number }[] | undefined
): number | undefined {
  if (!versionHistory?.length) return undefined;
  for (let i = versionHistory.length - 1; i >= 0; i -= 1) {
    if (versionHistory[i]?.status === BudgetStatus.APPROVED) {
      return versionHistory[i]?.version;
    }
  }
  return undefined;
}

export const CategoriesSelection: React.FC<CategoriesSelectionProps> = ({
  onHoverChange,
  onCollapseChange,
  disabled = false,
}) => {
  const {
    activeBudget,
    setActiveCalcOption,
    activeBudgetDispatch,
    activeCalcOption,
    updateBudget,
    setCategoriesSnapshot,
  } = useBudget();

  const { tabActive, client, project } = useProject();
  const showAbbottAdditionalInfo = useMemo(
    () => isAbbottClient(client?.clientSelected?.name ?? project?.client?.name),
    [client?.clientSelected?.name, project?.client?.name]
  );
  const [eventDataModal, setEventDataModal] = useState(false);

  const options = useMemo(() => {
    const itemTypeOption = createOptionsFromEnum(BudgetItemTypeEnum);
    const itemsWithType = itemTypeOption.map((el: any) => ({
      ...el,
      type: el.value,
    }));
    const reorderedOptions = sortItemsByCategory(itemsWithType, "type").filter(
      (el: any) => el.value !== "TRAVEL_INSURANCE"
    );

    return reorderedOptions.map((el: any, index: number) => {
      if (index === reorderedOptions.length - 1)
        return {
          title: el.label,
          value: el.value,
          description: "",
          icon: BudgetIcons[el.value],
          template: <BudgetTotals />,
        };
      return {
        title: el.label,
        value: el.value,
        description: "",
        icon: BudgetIcons[el.value],
        template: <></>,
      };
    });
  }, []);

  useEffect(() => {
    setCategoriesSnapshot(options || []);
  }, [options, setCategoriesSnapshot]);
  // Editor-only fields (generalInfo, etc.) live on activeBudget — list rows may be summaries.
  const currentBudget = activeBudget;

  const generalInfo = useMemo(() => {
    const normalizeInfo = (entry: any) => {
      if (!entry) return {};
      if (entry?.info && typeof entry.info === "object") {
        return { ...entry.info, version: entry.version };
      }
      return entry;
    };

    const entries = Array.isArray(currentBudget?.generalInfo)
      ? currentBudget.generalInfo
      : [];

    if (tabActive === TabActive.BUDGET) {
      const info = entries.find((i: any) => i?.version === 0) || entries[0];
      return normalizeInfo(info);
    }
    if (tabActive === TabActive.NEGOTIATION) {
      const info =
        entries.find(
          (i: any) => i?.version === currentBudget.negotiationVersion
        ) || entries[0];
      return normalizeInfo(info);
    }
    if (tabActive === TabActive.APPROVED) {
      const approvedIndex = findLastApprovedVersion(
        currentBudget?.versionHistory
      );
      const info =
        entries.find((i: any) => i?.version === approvedIndex) || entries[0];
      return normalizeInfo(info);
    }
    return normalizeInfo(entries[0]);
  }, [currentBudget, tabActive]);

  const updateBudgetLocal = async (payload: any) => {
    const budgetToUpdate = currentBudget?._id ? currentBudget : activeBudget;
    if (!budgetToUpdate?._id) {
      toast.error("Orçamento inválido para salvar os dados do evento");
      throw new Error("INVALID_BUDGET");
    }

    const approvedIndex = findLastApprovedVersion(
      currentBudget?.versionHistory
    );

    const versionToChange =
      tabActive === TabActive.BUDGET
        ? 0
        : tabActive === TabActive.NEGOTIATION
        ? currentBudget.negotiationVersion
        : approvedIndex ?? 0;

    const currentGeneralInfo = Array.isArray(budgetToUpdate.generalInfo)
      ? budgetToUpdate.generalInfo
      : [];
    const entryWithVersion = { ...payload, version: versionToChange };
    const hasCurrentVersionEntry = currentGeneralInfo.some((entry: any) => {
      const entryVersion = entry?.version;
      if (entryVersion === versionToChange) return true;
      return entryVersion == null && versionToChange === 0;
    });

    const mergedGeneralInfo = hasCurrentVersionEntry
      ? currentGeneralInfo.map((entry: any) => {
          const entryVersion = entry?.version;
          const isSameVersion =
            entryVersion === versionToChange ||
            (entryVersion == null && versionToChange === 0);
          return isSameVersion ? entryWithVersion : entry;
        })
      : [...currentGeneralInfo, entryWithVersion];

    const result = await updateBudget(
      budgetToUpdate._id,
      { ...budgetToUpdate, generalInfo: mergedGeneralInfo },
      "SAVE",
      -1
    );
    if (result == null) {
      throw new Error("SAVE_FAILED");
    }

    toast.success("Dados salvos com sucesso");
  };

  // --- visual states ---
  const [collapsed, setCollapsed] = useState(true);
  const [hoveringOverlay, setHoveringOverlay] = useState(false);

  useEffect(() => {
    onCollapseChange?.(collapsed);
  }, [collapsed]);

  const leaveTimer = useRef<number | null>(null);
  const clearLeaveTimer = () => {
    if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
  };

  const containerRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<HTMLDivElement | null>(null);
  const overlayContentRef = useRef<HTMLDivElement | null>(null);
  const overlayMode = collapsed;

  const onHandleEnter = () => {
    if (!collapsed) return;
    clearLeaveTimer();
    setHoveringOverlay(true);
    onHoverChange?.(true);
  };
  const onHandleLeave = () => {
    if (!collapsed) return;
    clearLeaveTimer();
    leaveTimer.current = window.setTimeout(() => {
      setHoveringOverlay(false);
      onHoverChange?.(false);
    }, 120);
  };
  const onOverlayEnter = () => {
    if (!collapsed) return;
    clearLeaveTimer();
    setHoveringOverlay(true);
    onHoverChange?.(true);
  };
  const onOverlayLeave = () => {
    if (!collapsed) return;
    clearLeaveTimer();
    leaveTimer.current = window.setTimeout(() => {
      setHoveringOverlay(false);
      onHoverChange?.(false);
    }, 120);
  };

  const ExpandedSidebar = (
    <Box
      ref={overlayContentRef}
      display="flex"
      flexDirection="column"
      gap={1.5}
      px="24px"
      pt="24px"
      pb="16px"
      borderRight="1px solid #DBDADE"
      sx={{
        width: "100%",
        boxSizing: "border-box",
        bgcolor: "background.paper",
        overflowWrap: "break-word",
        wordBreak: "break-word",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "row", minWidth: 0 }}>
        <Button
          variant="outlined"
          startIcon={<DescriptionIcon width={16} height={16} />}
          sx={{
            width: "100%",
            minWidth: 0,
            height: "auto",
            py: 0.75,
            whiteSpace: "normal",
            textAlign: "left",
            fontSize: "12px",
          }}
          onClick={() => setEventDataModal(true)}
        >
          Local do orçamento
        </Button>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <LeftSideBarHeader
          value={(activeCalcOption as HeaderOptions) ?? "A"}
          onChange={(t) => setActiveCalcOption(t)}
        />
      </Box>

      <Divider />

      <Stack spacing={1.5}>
        {options.map((el: any, index: any) => (
          <VerticalTab.Item key={el.value ?? index}>
            <VerticalTab.Icon
              icon={el.icon}
              isActive={false}
              color={
                activeBudget &&
                activeBudget?.itemTypeList?.[activeCalcOption]?.[el.value] ===
                  true
                  ? "#1361A4"
                  : activeBudget?.itemTypeList?.[activeCalcOption]?.[
                      el.value
                    ] === false
                  ? "#EFDBDB"
                  : undefined
              }
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <VerticalTab.Content
                title={el.title}
                description={el.description}
              />
            </Box>
            <Box display="flex" flexShrink={0} justifyContent="flex-end">
              <YesOrNoSwitch
                type={el.value}
                value={
                  activeBudget?.itemTypeList?.[activeCalcOption]?.[el.value]
                }
                disabled={disabled || activeCalcOption === "budgetTotals"}
                setValue={(type: string, newValue: boolean) => {
                  activeBudgetDispatch({
                    type: "YES_NO_SWITCH_CHANGE",
                    payload: { type, newValue, activeCalcOption },
                  });
                }}
              />
            </Box>
          </VerticalTab.Item>
        ))}
      </Stack>
    </Box>
  );

  const CollapsedSidebar = (
    <Box
      display="flex"
      flexDirection="column"
      gap={1.5}
      px="12px"
      pt="24px"
      pb="44px"
      borderRight="1px solid #DBDADE"
    >
      <Tooltip
        title={<span style={{ fontSize: "16px" }}>Dados do evento</span>}
        placement="right"
        PopperProps={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -8],
              },
            },
          ],
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: 40,
            height: 40,
            border: "1px solid #1976D2",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 1,
            marginLeft: 1,
            cursor: "pointer",
          }}
          onClick={() => {
            setEventDataModal(true);
          }}
        >
          <DescriptionIcon width={16} height={16} />
        </Box>
      </Tooltip>

      <Box sx={{ marginLeft: 1 }}>
        {/* <CalcOption
          type={(activeCalcOption as HeaderOptions) ?? "A"}
          isActive
          setIsActive={() => null}
        /> */}
        <LeftSideBarHeaderCollapsed
          value={(activeCalcOption as HeaderOptions) ?? "A"}
          onChange={(t) => setActiveCalcOption(t)}
        />
      </Box>

      <Divider />
      <Stack spacing={1.5}>
        {options.map((el: any, index: any) => (
          <Tooltip
            key={el.value ?? index}
            title={<span style={{ fontSize: "16px" }}>{el?.title}</span>}
            placement="right"
            PopperProps={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, -8],
                  },
                },
              ],
            }}
          >
            <Box>
              <VerticalTab.Item
                onClick={() => {
                  if (disabled || activeCalcOption === "budgetTotals") return;

                  const currentItem: any =
                    activeBudget?.itemTypeList?.[activeCalcOption];
                  const currentItemValue = currentItem?.[el?.value];

                  const newValue =
                    currentItemValue === undefined
                      ? true
                      : currentItemValue === true
                      ? false
                      : true;

                  activeBudgetDispatch({
                    type: "YES_NO_SWITCH_CHANGE",
                    payload: { type: el.value, newValue, activeCalcOption },
                  });
                }}
              >
                <VerticalTab.Icon
                  icon={el.icon}
                  isActive={false}
                  color={
                    activeBudget &&
                    activeBudget?.itemTypeList?.[activeCalcOption]?.[
                      el.value
                    ] === true
                      ? "#1361A4"
                      : activeBudget?.itemTypeList?.[activeCalcOption]?.[
                          el.value
                        ] === false
                      ? "#EFDBDB"
                      : undefined
                  }
                />
              </VerticalTab.Item>
            </Box>
          </Tooltip>
        ))}
      </Stack>
    </Box>
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        position: "relative",
        flex: 1,
        minHeight: 0,
        height: "100%",
        alignSelf: "stretch",
      }}
    >
      <BudgetDataModal
        isOpen={eventDataModal}
        info={generalInfo}
        onClose={() => setEventDataModal(false)}
        onConfirm={updateBudgetLocal}
        showAbbottAdditionalInfo={showAbbottAdditionalInfo}
      />

      {/* Área scrollável - scrollbar à esquerda (direction: rtl) */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          direction: "rtl",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0, 0, 0, 0.25) transparent",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0, 0, 0, 0.25)",
            borderRadius: "3px",
          },
        }}
      >
        <Box
          sx={{
            direction: "ltr",
            visibility: collapsed && hoveringOverlay ? "hidden" : "visible",
            pointerEvents: collapsed && hoveringOverlay ? "none" : "auto",
          }}
        >
          {collapsed ? CollapsedSidebar : null}
          {!collapsed ? ExpandedSidebar : null}
        </Box>
      </Box>

      <Box
        ref={handleRef}
        sx={{
          alignSelf: "center",
          height: "50%",
          width: 14,
          flexShrink: 0,
          backgroundColor: collapsed ? "#536574" : "#1361A4",
          cursor: "pointer",
          borderTopRightRadius: "8px",
          borderBottomRightRadius: "8px",
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
          zIndex: 2,
        }}
        onClick={() => {
          setCollapsed((c) => {
            if (c) return false; // expandindo
            setHoveringOverlay(false);
            onHoverChange?.(false);
            return true; // recolhendo
          });
        }}
        onMouseEnter={onHandleEnter}
        onMouseLeave={onHandleLeave}
        title={
          collapsed ? "Expandir (hover) / Fixar (clique)" : "Recolher (clique)"
        }
      >
        {collapsed ? (
          <RiArrowRightSFill color="white" />
        ) : (
          <FaThumbtack color="white" size={14} />
        )}
      </Box>

      {overlayMode && hoveringOverlay && (
        <Box
          onMouseEnter={onOverlayEnter}
          onMouseLeave={onOverlayLeave}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 334,
            display: "flex",
            flexDirection: "row",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            boxShadow:
              "0px 3px 6px rgba(0,0,0,0.16), 0px 6px 12px rgba(0,0,0,0.12)",
            borderRadius: 1,
            bgcolor: "background.paper",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              overflowY: "auto",
              overflowX: "hidden",
              width: "100%",
              maxWidth: 320,
              direction: "rtl",
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(0, 0, 0, 0.25) transparent",
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0, 0, 0, 0.25)",
                borderRadius: "3px",
              },
            }}
          >
            <Box sx={{ direction: "ltr" }}>{ExpandedSidebar}</Box>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              width: 14,
              height: "50%",
              alignSelf: "center",
              backgroundColor: "#536574",
              cursor: "pointer",
              borderTopRightRadius: "8px",
              borderBottomRightRadius: "8px",
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
            }}
            onMouseEnter={onOverlayEnter}
            onMouseLeave={onOverlayLeave}
            onClick={() => setCollapsed(false)}
            title="Fixar (clique)"
          >
            <Unpinned />
          </Box>
        </Box>
      )}
    </Box>
  );
};
